import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, Leaf, Stethoscope, TrendingUp } from "lucide-react";

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  loading?: boolean;
}

function StatCard({ title, value, icon: Icon, loading }: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <div className="text-2xl font-bold">{value}</div>
        )}
      </CardContent>
    </Card>
  );
}

export function AdminStats() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => {
      const [profilesRes, plantsRes, diagnosticsRes, subscriptionsRes] =
        await Promise.all([
          supabase.from("profiles").select("id", { count: "exact", head: true }),
          supabase
            .from("plant_identifications")
            .select("id", { count: "exact", head: true }),
          supabase
            .from("plant_diagnostics")
            .select("id", { count: "exact", head: true }),
          supabase
            .from("subscriptions")
            .select("id", { count: "exact", head: true })
            .eq("plan_type", "premium"),
        ]);

      return {
        totalUsers: profilesRes.count || 0,
        totalPlants: plantsRes.count || 0,
        totalDiagnostics: diagnosticsRes.count || 0,
        premiumUsers: subscriptionsRes.count || 0,
      };
    },
  });

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <StatCard
        title="Total de Usuários"
        value={stats?.totalUsers ?? 0}
        icon={Users}
        loading={isLoading}
      />
      <StatCard
        title="Plantas Identificadas"
        value={stats?.totalPlants ?? 0}
        icon={Leaf}
        loading={isLoading}
      />
      <StatCard
        title="Diagnósticos"
        value={stats?.totalDiagnostics ?? 0}
        icon={Stethoscope}
        loading={isLoading}
      />
      <StatCard
        title="Usuários Premium"
        value={stats?.premiumUsers ?? 0}
        icon={TrendingUp}
        loading={isLoading}
      />
    </div>
  );
}
